import { createSlice } from "@reduxjs/toolkit";
import { MenuCategory, MenuItem } from "src/interfaces/menu.interface";

interface CategoryState {
  menuCategories: MenuCategory[];
  filteredMenuCategories: MenuCategory[];
  allItems: MenuItem[];
  filteredItems: MenuItem[];
  hasMenuCategoryId: string | null;
  isMenuCategoryLoading: boolean;
  categoriesRevision: number;
}

const initialState: CategoryState = {
  menuCategories: [],
  filteredMenuCategories: [],
  allItems: [] as MenuItem[],
  filteredItems: [] as MenuItem[],
  hasMenuCategoryId: null,
  isMenuCategoryLoading: false,
  /** Incremented when server categories should be re-fetched on MenuCategoriesPage (local list). */
  categoriesRevision: 0,
};

const getCategoryName = (category: any) =>
  (category?.name || "").trim().toLowerCase();

const sortCategoriesByName = (categories: any[]) =>
  categories.slice().sort((previousCategory, nextCategory) => {
    const nameCompare = getCategoryName(previousCategory).localeCompare(
      getCategoryName(nextCategory)
    );

    if (nameCompare !== 0) return nameCompare;

    return (previousCategory?.correlationId || "").localeCompare(
      nextCategory?.correlationId || ""
    );
  });

export const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    set_menu_categories: (state: any, { payload }) => {
      const categories = sortCategoriesByName(payload.categories ?? []);

      state.menuCategories = categories;
      state.filteredMenuCategories = categories;
      // Flatten all items from all categories
      const allItems: MenuItem[] = [];
      categories.forEach((category: any) => {
        if (category.items && Array.isArray(category.items)) {
          category.items.forEach((item: MenuItem) => {
            allItems.push(item);
          });
        }
      });
      state.allItems = allItems;
      state.filteredItems = allItems;
    },
    search_menu_categories: (state, { payload }) => {
      state.filteredMenuCategories = sortCategoriesByName(
        state.menuCategories.filter((category: any) =>
          getCategoryName(category).includes(payload.toLowerCase())
        )
      );
    },
    search_items: (state, { payload }) => {
      const searchTerm = payload?.toLowerCase() || "";
      if (!searchTerm) {
        state.filteredItems = state.allItems;
      } else {
        state.filteredItems = state.allItems.filter((item: MenuItem) =>
          item.englishName?.toLowerCase().includes(searchTerm) ?? false
        );
      }
    },
    delete_menu_category_modal: (state: any, { payload }) => {
      state.hasMenuCategoryId = payload;
    },
    sort_menu_categories: (state, { payload }) => {
      if (payload === "aToZ") {
        state.filteredMenuCategories = sortCategoriesByName(state.menuCategories);
      } else if (payload === "zToA") {
        state.filteredMenuCategories = sortCategoriesByName(state.menuCategories).reverse();
      }
    },
     set_menu_category_loading:(state, { payload }) => {
      state.isMenuCategoryLoading = payload;      
    },
    bump_categories_revision: (state: any) => {
      state.categoriesRevision += 1;
    },
  },
});

export const {
  set_menu_categories,
  search_menu_categories,
  search_items,
  delete_menu_category_modal,
  sort_menu_categories,
  set_menu_category_loading,
  bump_categories_revision,
} = categorySlice.actions;

export default categorySlice.reducer;
